import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import WebinarService from "../../Services/WebinarService";

export default function EditWebinar({
  show,
  onHide,
  onSuccess,
  webinarData,
  pageTitle = "Event/Webinar",
}) {
  const imageRef = useRef(null);
  const [editData, setEditData] = useState({
    imageFile: null,
    currentImagePath: "",
    title: "",
    type: "",
    location: "",
    date: "",
    description: "",
  });
  const [validated, setValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (webinarData && show) {
      setEditData({
        imageFile: null,
        currentImagePath: webinarData.imagePath || "",
        title: webinarData.title || "",
        type: webinarData.type || "",
        location: webinarData.location || "",
        date: webinarData.date
          ? new Date(webinarData.date).toISOString().slice(0, 16)
          : "",
        description: webinarData.description || "",
      });
    }
  }, [webinarData, show]);

  const handleImageInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG)");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setEditData((prev) => ({
      ...prev,
      imageFile: file,
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Updating...");

      const formData = new FormData();
      if (editData.imageFile) {
        formData.append("image", editData.imageFile);
      }
      formData.append("title", editData.title.trim());
      formData.append("type", editData.type.trim());
      formData.append("location", editData.location.trim());
      formData.append("date", editData.date.trim());
      formData.append("description", editData.description.trim());

      const response = await WebinarService.updateWebinar(
        webinarData.id,
        formData
      );

      if (response.data.success) {
        toast.success(response.data.message, { id: loadingMessage });
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(response.data.message || "Something went wrong", {
          id: loadingMessage,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setValidated(false);
    setEditData({
      imageFile: null,
      currentImagePath: "",
      title: "",
      type: "",
      location: "",
      date: "",
      description: "",
    });
    onHide();
  };

  const handleRemoveNewImage = () => {
    setEditData((prev) => ({
      ...prev,
      imageFile: null,
    }));
    // Reset the file input
    if (imageRef.current) {
      imageRef.current.value = "";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="lg">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          <i className="bi bi-pencil-square me-2"></i>
          Edit {pageTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            {/* Title */}
            <Col xs={12} md={6}>
              <Form.Group controlId="title">
                <Form.Label>Event/Webinar Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={editData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event or webinar title"
                  required
                  isInvalid={validated && !editData.title.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a title
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Type */}
            <Col xs={12} md={6}>
              <Form.Group controlId="type">
                <Form.Label>Type *</Form.Label>
                <Form.Control
                  type="text"
                  name="type"
                  value={editData.type}
                  onChange={handleInputChange}
                  placeholder="Enter type (e.g., Webinar, Workshop, etc.)"
                  required
                  isInvalid={validated && !editData.type.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a type
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Location */}
            <Col xs={12} md={6}>
              <Form.Group controlId="location">
                <Form.Label>Location *</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={editData.location}
                  onChange={handleInputChange}
                  placeholder="Enter location (e.g.,City, Venue)"
                  required
                  isInvalid={validated && !editData.location.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a location
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Date */}
            <Col xs={12} md={6}>
              <Form.Group controlId="date">
                <Form.Label>Event Date & Time *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="date"
                  value={editData.date}
                  onChange={handleInputChange}
                  required
                  isInvalid={validated && !editData.date.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Please select event date and time
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Description */}
            <Col xs={12} md={12}>
              <Form.Group controlId="description">
                <Form.Label> Event Theme *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={editData.description}
                  onChange={handleInputChange}
                  placeholder="Enter event or webinar description"
                  required
                  isInvalid={validated && !editData.description.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Image */}
            <Col xs={12} md={12}>
              <Form.Label>Event Image</Form.Label>

              {/* Show current image */}
              {editData.currentImagePath && !editData.imageFile && (
                <div className="mt-2">
                  <p className="text-muted small mb-2">Current Image:</p>
                  <img
                    src={editData.currentImagePath}
                    alt="Current Event"
                    style={{ maxWidth: "90%", maxHeight: "200px" }}
                  />
                </div>
              )}

              {/* Show new image preview */}
              {editData.imageFile ? (
                <div className="mt-2">
                  <p className="text-muted small mb-2">New Image:</p>
                  <div className="d-flex position-relative">
                    <img
                      src={URL.createObjectURL(editData.imageFile)}
                      alt="New Upload"
                      style={{ maxWidth: "90%", maxHeight: "200px" }}
                    />
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{
                        position: "absolute",
                        right: "35px",
                        top: "-13px",
                      }}
                      onClick={handleRemoveNewImage}
                    >
                      <i className="bi bi-x-circle-fill text-danger"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label
                    htmlFor="importInput_image_edit"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-cloud-plus me-2"></i> Upload New Image
                  </label>
                  <input
                    id="importInput_image_edit"
                    type="file"
                    className="d-none"
                    ref={imageRef}
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageInput}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to keep current image. Recommended size:
                    800x600px
                  </Form.Text>
                </div>
              )}
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Update Event
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
